import { jwtDecode } from "jwt-decode";
import type { JwtPayload } from "./types";
import { state } from "./state";
import { defineStoreActions } from "~/stores";
import { id } from "./id";
import { getters } from "./getters";
import { repository } from "~/repository";
import type {
  RequestLoginPayload,
  RequestRegisterPayload,
} from "~/repository/auth.types";
import { useAuthStore } from "~/stores/auth/store";
import { useProcessStore } from "~/stores/process/store";
import moment from "moment";

export const actions = defineStoreActions(
  {
    id,
    state,
    getters,
  },
  {
    async requestLogin(_options: RequestLoginPayload) {
      const processStore = useProcessStore();

      const ott_code = _options?.ott_code;

      try {
        const { meta } = await repository.auth.requestLogin(_options);
        this.setAuthenticated(meta.token);
        await this.fetchMe();

        if (ott_code) {
          await navigateTo(`ott/login/complete`);
        } else {
          await processStore.handleRedirection("/");
        }
      } catch (e: any) {
        if (e.response?.status === 428) {
          const token = e?.response?._data?.meta?.token;
          if (token) this.setAuthenticated(token);

          if (ott_code) {
            await navigateTo(`ott/login/complete`);
          } else {
            processStore.setRedirectUrl("/");
            await navigateTo("/register/verify-email");
          }
        } else {
          throw e;
        }
      }
    },
    async requestRegister(_options: RequestRegisterPayload) {
      const processStore = useProcessStore();
      const ott_code = _options.data.attributes?.ott_code;

      try {
        const { meta } = await repository.auth.requestRegister(_options);
        this.setAuthenticated(meta.token);
        await this.fetchMe();
        if (ott_code) {
          await navigateTo(`ott/register/complete`);
        } else if (processStore.selectedPlan) {
          await navigateTo("/register/checkout");
        } else {
          await navigateTo("/register/select-plan");
        }
      } catch (e: any) {
        if (e.response?.status === 428) {
          const token = e?.response?._data?.meta?.token;
          if (token) this.setAuthenticated(token);

          if (ott_code) {
            await navigateTo(`/register/complete?ott_code=${ott_code}`);
          } else {
            await navigateTo("/register/verify-email");
          }
        } else {
          throw e;
          // Show error message
        }
      }
    },
    setAuthenticated(token: string) {
      useCookie("access_token", {
        maxAge: 60 * 60 * 24 * 365,
      }).value = token;

      const tokenData: JwtPayload = jwtDecode(token);
      this.$patch((state) => {
        state.authenticated = true;
        state.user.email = tokenData.email;
        state.user.emailVerified = tokenData.email_verified ?? true;
        state.user.mobileVerified = tokenData?.sms_verified ?? false;
        state.user.username = tokenData.user_name;
        state.user.subscriberID = tokenData.sub;
        state.user.phoneNumber = tokenData.phone_number || "";
        state.user.notificationsEnabled =
          tokenData.notifications_enabled ?? false;

        state.subscription.interval = tokenData.subscription_level;
        state.subscription.store = tokenData.store;

        state.user.firstName = tokenData.first_name;
        state.user.lastName = tokenData.last_name;
        state.user.dateOfBirth = tokenData.date_of_birth
          ? moment(tokenData.date_of_birth, "YYYY-MM-DD").format("MM/DD/YYYY")
          : "";
      });
    },
    async fetchMe() {
      const { data } = await repository.auth.fetchMe();
      this.$patch((state) => {
        state.user = {
          email: data.basic.email,
          username: data.basic.user_name,
          emailVerified: !!data.basic.email_verified,
          mobileVerified: data.basic.sms_verified ?? false,
          updatedAt: data.basic.updated_at,
          firstName: data.basic.first_name,
          lastName: data.basic.last_name,
          phoneNumber: data.basic.phone_number || "",
          subscriberID: state.user.subscriberID,
          address: data.basic.address,
          city: data.basic.city,
          state: data.basic.state,
          postalCode: data.basic.postal_code,
          notificationsEnabled: state.user.notificationsEnabled,
          dateOfBirth: moment(data.basic.date_of_birth, "YYYY-MM-DD").format(
            "MM/DD/YYYY",
          ),
        };
      });
      return data.basic;
    },
    handleLogout(reload: boolean = true) {
      useCookie("access_token").value = null;
      useAuthStore().$reset();

      if (reload) {
        window.location.reload();
      }
    },
  },
);
